import { CardRow, CardRowProps } from 'components/CardRow';
import { MenuProps } from 'components/Menu';
import { Pill } from 'components/Pill';
import { Txt } from 'components/Txt';
import { formatDate } from 'date-fns';
import {
  UI_DATE_FORMAT_DDMMYYYY,
  UI_DATE_FORMAT_LONG,
  ScreenSize,
} from 'lib/constants';
import { filterArrayEmpty, isoDateStringToDate } from 'lib/riskbase';
import { theme } from 'lib/theme';
import { View } from 'react-native';
import { useScreenSize } from 'hooks/useScreenSize';
import {
  ContactAddedVia,
  EngageContactArchivedReason,
  EngageFlatContact,
  EngageFlatListItem,
  FlatContactTypes,
} from 'types/Engage';
import { pluraliseS } from 'lib/pluralise';
import { getFlatContactConfirmationInfo } from 'store/remote/engageUtils';

type Props = {
  menuOptions?: MenuProps['options'];
  checkedValue?: CardRowProps['checkedValue'];
  contactDetailsOnOwnLine?: boolean;
  showAddedInfo?: boolean;
  showArchivedInfo?: boolean;
} & (
  | {
      flatListContact: false;
      contact: EngageFlatContact;
      onPress?: (contact: EngageFlatContact) => void;
    }
  | {
      flatListContact: true;
      contact: EngageFlatListItem['contacts'][number];
      onPress?: (contact: EngageFlatListItem['contacts'][number]) => void;
    }
);

const getArchivedReasonText = (
  contact: Pick<EngageFlatContact, 'archivedReason'>,
) => {
  switch (contact.archivedReason) {
    case EngageContactArchivedReason.mistake:
      return `Added by mistake`;
    case EngageContactArchivedReason.movedOut:
      return `No longer lives in this flat`;
    case EngageContactArchivedReason.stoppedOwning:
      return `No longer owns this flat`;
    case EngageContactArchivedReason.movedOutStoppedOwning:
      return `No longer owns and lives in this flat`;
    case EngageContactArchivedReason.notMe:
      return `Did not identify with this name`;
    case EngageContactArchivedReason.other:
    default:
      return `Archived for an unknown reason`;
  }
};

export const EngageContactCard: React.FC<Props> = ({
  contact,
  onPress,
  menuOptions,
  checkedValue,
  contactDetailsOnOwnLine = false,
  showAddedInfo = false,
  showArchivedInfo = false,
  flatListContact,
}) => {
  const contactType = FlatContactTypes[contact.contactType];
  const { daysUntilExpiry, confirmedAndInDate } =
    getFlatContactConfirmationInfo(contact);

  const confirmedDateLabel = contact.confirmedDate
    ? formatDate(
        isoDateStringToDate(contact.confirmedDate),
        UI_DATE_FORMAT_LONG,
      )
    : null;

  const daysUntilExpiryLabel =
    daysUntilExpiry !== null
      ? daysUntilExpiry < 0
        ? ` (Expired ${pluraliseS(Math.abs(daysUntilExpiry), 'day')} ago)`
        : daysUntilExpiry > 0
          ? ` (Expires in ${pluraliseS(daysUntilExpiry, 'day')})`
          : ' (Expires today)'
      : '';

  const confirmationLabel = contact.confirmedDate
    ? confirmedDateLabel
      ? `Confirmed ${confirmedDateLabel} ${daysUntilExpiryLabel}`
      : 'Confirmed'
    : 'Not Confirmed';

  const confirmationPillBackground = confirmedAndInDate
    ? theme.colors.green
    : theme.colors.red;
  const confirmationPillTextColor = theme.colors.white;
  const largeScreen = useScreenSize() === ScreenSize.large;

  const TxtType = (
    <Txt
      style={{
        color: theme.colors.fieldValueText,
      }}
      size="xsmall"
    >
      {contactType.name}
    </Txt>
  );

  const addedInfo =
    showAddedInfo && !flatListContact
      ? `Added ${formatDate(
          isoDateStringToDate(contact.addedTimestamp),
          UI_DATE_FORMAT_LONG,
        )} via ${ContactAddedVia[contact.addedVia].name}`
      : '';

  const archivedInfo =
    showArchivedInfo &&
    !flatListContact &&
    contact.archivedTimestamp &&
    contact.archivedVia
      ? `Archived ${formatDate(
          isoDateStringToDate(contact.archivedTimestamp),
          UI_DATE_FORMAT_DDMMYYYY,
        )} via ${ContactAddedVia[contact.archivedVia].name}`
      : '';
  const archivedReason =
    showArchivedInfo && !flatListContact && contact.archivedTimestamp
      ? getArchivedReasonText(contact)
      : '';

  const infoText = [addedInfo, archivedInfo, archivedReason]
    .filter(filterArrayEmpty)
    .join(largeScreen ? ' • ' : '\n');

  return (
    <CardRow
      onPress={
        onPress
          ? flatListContact
            ? () => onPress(contact)
            : () => onPress(contact)
          : undefined
      }
      menuOptions={menuOptions}
      checkedValue={checkedValue}
    >
      <Txt>
        <Txt key="name" weight="medium">
          {contact.isUnder16
            ? 'Under 16'
            : flatListContact
              ? contact.name
              : `${contact.firstName} ${contact.lastName}`}
        </Txt>
        {!!contact.email || (!flatListContact && !!contact.phone)
          ? contactDetailsOnOwnLine
            ? '\n'
            : ' • '
          : ''}
        {[contact.email, !flatListContact ? contact.phone : '']
          .filter(filterArrayEmpty)
          .join(' • ')}
      </Txt>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.paddingHorizontalHalf,
          flexWrap: 'wrap',
        }}
      >
        {TxtType}
        <Pill
          backgroundColor={confirmationPillBackground}
          textColor={confirmationPillTextColor}
          label={confirmationLabel}
          textProps={{
            size: 'xxxsmall',
            weight: 'bold',
          }}
        />
        {contact.hasSupportNeeds && (
          <Pill
            backgroundColor={theme.colors.orange}
            label="Has Support Needs"
            textProps={{
              size: 'xxxsmall',
              weight: 'bold',
            }}
          />
        )}
      </View>
      {!!infoText && (
        <Txt
          style={{
            color: theme.colors.fieldValueText,
          }}
          size="xsmall"
        >
          {infoText}
        </Txt>
      )}
    </CardRow>
  );
};
